import React from 'react';
import {BaseEdge, BezierEdge, type EdgeProps} from 'reactflow';

import {assertNever} from 'util/Types';

type LayoutDirection = 'LR' | 'RL' | 'TB' | 'BT';

interface PathArgs {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  layoutDirection: LayoutDirection;
}

/**
 * Determines if an edge goes in the reverse direction of the layout.
 * For example, in an LR (left-to-right) layout, an edge is "reverse" if it
 * starts on the right and ends on the left (going backwards).
 */
export function isReverseEdge({sourceX, sourceY, targetX, targetY, layoutDirection}: PathArgs): boolean {
  switch (layoutDirection) {
    case 'LR':
      return sourceX >= targetX;
    case 'RL':
      return sourceX <= targetX;
    case 'TB':
      return sourceY >= targetY;
    case 'BT':
      return sourceY <= targetY;
    default:
      throw new Error(`Unknown layoutDirection ${assertNever(layoutDirection)}`);
  }
}

/**
 * Generates an SVG path string for a self-referencing edge (loop) that adapts
 * to different layout directions. The path creates an elliptical arc that curves
 * away from the node with appropriate offsets for visual clarity.
 */
export function getReverseEdgePath({sourceX, sourceY, targetX, targetY, layoutDirection}: PathArgs): string {
  let radiusX = 50;
  let radiusY = 50;
  let sweepFlag = 0;

  switch (layoutDirection) {
    case 'LR':
      radiusX = (sourceX - targetX) * 0.6;
      break;
    case 'RL':
      radiusX = (targetX - sourceX) * 0.6;
      sweepFlag = 1;
      break;
    case 'TB':
      radiusY = (sourceY - targetY) * 0.6;
      break;
    case 'BT':
      radiusY = (targetY - sourceY) * 0.6;
      sweepFlag = 1;
      break;
    default:
      throw new Error(`Unknown layoutDirection ${assertNever(layoutDirection)}`);
  }

  return `M ${sourceX} ${sourceY} A ${radiusX} ${radiusY} 0 1 ${sweepFlag} ${targetX} ${targetY}`;
}

/**
 * Renders an edge within the entity map with special-case handling for links involved in a cycle.
 * In this case we use a special offset path for the link that is drawn backwards or against the grain.
 * NOTE: We currently assume a left->right layout.
 */
export default function EntityMapEdge(props: EdgeProps): JSX.Element {
  const {sourceX, sourceY, targetX, targetY} = props;
  const pathArgs: PathArgs = {sourceX, sourceY, targetX, targetY, layoutDirection: 'LR'};
  if (isReverseEdge(pathArgs)) {
    const path = getReverseEdgePath(pathArgs);
    return <BaseEdge path={path} {...props} />;
  }
  return <BezierEdge {...props} />;
}
